import React, {useMemo} from 'react';
import type {SubLabel} from '../../types';
import type {BoxProps} from '@tempo/core';
import {Tr} from '@tempo/core';
import {useTranslation} from 'next-i18next';
import type {Column} from '@components/Table/types';
import type {TableContextType} from '@components/Table';
import Table, {TableContext} from '@components/Table';
import TableHeader from '@components/Table/core/TableHeader';
import HeaderCell from '@components/Table/cells/HeaderCell';
import {LabelTableColumn} from './types';
import LabelTableBody from './LabelTableBody';

type LabelTableProps = {
	labels: SubLabel[];
	onLabelEdit: (label: SubLabel) => void;
	onLabelRemove: (label: SubLabel) => void;
} & BoxProps;

const LabelTableContext = TableContext as TableContextType<LabelTableColumn>;

const LabelTable = ({
	labels,
	onLabelEdit,
	onLabelRemove,
	...rest
}: LabelTableProps) => {
	const {t} = useTranslation('d4c');
	const columns: Column<LabelTableColumn>[] = useMemo(() => {
		return [
			{
				id: LabelTableColumn.NAME,
				label: t('analyticsartist_text_name_d4c-web'),
				width: '160px',
			},
			{
				id: LabelTableColumn.UPC,
				label: t('deliverymonitoring_text_upc_d4c-web'),
				display: {base: false, sm: true},
				width: '160px',
			},
			{
				id: LabelTableColumn.EDIT,
				width: '48px',
			},
			{
				id: LabelTableColumn.DELETE,
				width: '48px',
			},
		];
	}, [t]);

	return (
		<Table
			columns={columns}
			flex={1}
			layout="fixed"
			variant="contained"
			{...rest}
		>
			<LabelTableContext.Consumer>
				{({columns}) => (
					<>
						<TableHeader>
							<Tr>
								{columns.map(column => {
									return (
										<HeaderCell
											key={column.id}
											label={column.label || ''}
											width={column.width}
											display={column.display}
										/>
									);
								})}
							</Tr>
						</TableHeader>
						<LabelTableBody
							data={labels}
							onLabelEdit={onLabelEdit}
							onLabelRemove={onLabelRemove}
						/>
					</>
				)}
			</LabelTableContext.Consumer>
		</Table>
	);
};

export default LabelTable;
